import { Lock } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { RegisterToBidDialog } from "@/components/bidding/RegisterToBidDialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { mediaUrlFromMap } from "@/lib/api";
import { localized } from "@/lib/intl";

/*
 * B4: the private auction gate. Anyone with the link sees a refined door —
 * never lots, prices or the microsite. Access states: sign in → request →
 * pending → approved (the landing reloads itself via the event socket).
 */
export const PrivateGate = ({ data, onReload }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();
  const location = useLocation();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [status, setStatus] = useState(data.my_registration_status);

  const hero = mediaUrlFromMap(data.media, data.event.hero_media_id);
  const title = localized(data.event.title, locale);
  const description = localized(data.event.description, locale);

  const onRegistered = (reg) => {
    setStatus(reg.status);
    if (reg.status === "approved") onReload();
  };

  return (
    <div className="relative min-h-[78vh] flex items-center justify-center overflow-hidden" data-testid="private-gate">
      {hero && (
        <>
          <img src={hero} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0A]/70" />
        </>
      )}
      <div
        className={`relative z-10 w-full max-w-lg mx-6 my-20 rounded-3xl px-8 py-12 flex flex-col items-center text-center gap-5 ${
          hero ? "bg-white/95 backdrop-blur-xl shadow-[0_24px_80px_rgb(0,0,0,0.35)]" : "bg-white border border-black/5 shadow-[0_16px_40px_rgb(0,0,0,0.06)]"
        }`}
      >
        <span className="w-14 h-14 rounded-full bg-[#1D4D3B]/8 border border-[#1D4D3B]/15 flex items-center justify-center">
          <Lock className="w-6 h-6 text-[#1D4D3B]" aria-hidden="true" />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#1D4D3B]">{t("gate.eyebrow")}</span>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A]" data-testid="gate-event-title">
          {title}
        </h1>
        {data.organizer?.name && <p className="text-sm text-[#737373]">{t("pub.by", { name: data.organizer.name })}</p>}
        {description && <p className="text-sm text-[#0A0A0A]/60 leading-relaxed max-w-sm">{description}</p>}

        <div className="w-full pt-2">
          {status === "pending" ? (
            <div className="rounded-2xl bg-amber-500/[0.08] border border-amber-500/20 px-6 py-5 flex flex-col gap-1" data-testid="gate-pending">
              <span className="text-sm font-semibold text-[#0A0A0A]">{t("bid.pendingTitle")}</span>
              <span className="text-xs text-[#737373]">{t("bid.pendingDesc")}</span>
            </div>
          ) : status === "declined" ? (
            <div className="rounded-2xl bg-black/[0.04] px-6 py-5" data-testid="gate-declined">
              <span className="text-sm text-[#737373]">{t("bid.registrationDeclined")}</span>
            </div>
          ) : !user ? (
            <Link
              to={`/signin?next=${encodeURIComponent(location.pathname)}`}
              data-testid="gate-signin-link"
              className="inline-flex items-center justify-center w-full rounded-full bg-[#0A0A0A] text-white text-[17px] px-[22px] py-[12px] hover:bg-[#2b2b2b] transition-colors duration-300"
            >
              {t("gate.signin")}
            </Link>
          ) : (
            <Button
              onClick={() => setRegisterOpen(true)}
              data-testid="gate-request-access"
              className="w-full rounded-full bg-[#0A0A0A] text-white text-[17px] h-auto px-[22px] py-[12px] hover:bg-[#2b2b2b]"
            >
              {t("bid.requestAccess")}
            </Button>
          )}
        </div>
        <p className="text-xs text-[#737373]">{t("gate.desc")}</p>
      </div>

      <RegisterToBidDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        eventId={data.event.id}
        eventVisibility={data.event.visibility}
        terms={data.terms}
        requiredLevel={data.event.verification_level || 1}
        onRegistered={onRegistered}
      />
    </div>
  );
};
